import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { toast } from 'sonner';
import { 
  TrendingUp, 
  Users, 
  BookOpen, 
  Music, 
  Film, 
  Calendar,
  Euro,
  Plus,
  Edit,
  Trash2,
  Activity,
  TrendingDown,
  BarChart3,
  RefreshCw,
  Download
} from 'lucide-react';
import axios from 'axios';

// Use the global axios instance that already has auth configured
// Don't create a separate instance to ensure auth headers are included

const Dashboard = ({ currentUser, handleExportLoanHistory, refreshTrigger, authToken }) => {
  if (process.env.NODE_ENV !== 'production') {
    console.debug('Dashboard mount — user:', currentUser?.email, 'authToken:', authToken ? 'present' : 'missing');
  }
  const [analyticsData, setAnalyticsData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    description: '',
    amount: '',
    category: 'other',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [editingExpense, setEditingExpense] = useState(null);
  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false);

  const expenseCategories = [
    { value: 'transport', label: '🚗 Transport' },
    { value: 'catering', label: '🍽️ Catering' },
    { value: 'materials', label: '📦 Materials' },
    { value: 'staff', label: '👥 Staff' },
    { value: 'marketing', label: '📢 Marketing' },
    { value: 'venue', label: '🏢 Venue' },
    { value: 'equipment', label: '🎵 Equipment' },
    { value: 'other', label: '📋 Other' }
  ];

  useEffect(() => {
    console.log('🔍 DASHBOARD DEBUG: useEffect triggered, currentUser role:', currentUser?.role);
    if (currentUser?.role === 'Administrator') {
      console.log('🔍 DASHBOARD DEBUG: User is admin, calling fetchDashboardData');
      fetchDashboardData();
    } else {
      console.log('🔍 DASHBOARD DEBUG: User is not admin, skipping fetchDashboardData');
    }
  }, [currentUser]);

  // Auto-refresh dashboard when events are created/updated
  useEffect(() => {
    if (currentUser?.role === 'Administrator' && refreshTrigger > 0) {
      fetchDashboardData();
      toast.success("📊 Dashboard updated with latest event data!", {
        duration: 4000
      });
    }
  }, [refreshTrigger, currentUser]);

  // Helper function to check if event is newly created (within last 5 minutes)
  const isNewEvent = (event) => {
    if (!event) return false;
    
    // Only use creation timestamp fields, NOT event_date (which is when event happens, not when it was created)
    const creationTimestampFields = ['created_at', 'date_created', 'timestamp'];
    let timestampValue = null;
    let fieldFound = null;
    
    for (const field of creationTimestampFields) {
      if (event[field]) {
        timestampValue = event[field];
        fieldFound = field;
        break;
      }
    }
    
    if (!timestampValue) {
      console.log(`🕒 Event "${event.event_title}": No creation timestamp found (only event_date available) - can't determine if recently created`);
      return false; // Can't determine creation time, so don't highlight
    }
    
    try {
      // Parse the ISO timestamp from backend
      const eventTime = new Date(timestampValue);
      const now = new Date();
      const timeDiff = now - eventTime;
      const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
      
      // Make sure timestamp is in the past (not future)
      if (timeDiff < 0) {
        console.log(`🕒 Event "${event.event_title}": Timestamp is in the future - returning false`);
        return false;
      }
      
      const isNew = timeDiff < fiveMinutes;
      console.log(`🕒 Event "${event.event_title}": ${Math.round(timeDiff/60000)} minutes old, isNew: ${isNew}`);
      
      return isNew;
    } catch (error) {
      console.log(`🕒 Event "${event.event_title}": Parse error - returning false`);
      return false;
    }
  };

  const fetchDashboardData = async () => {
    console.log('🔍 DASHBOARD DEBUG: Starting to fetch dashboard data...');
    try {
      setLoading(true);
      
      const headers = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
      console.log('🔍 DASHBOARD DEBUG: Using headers:', headers);
      
      const [analyticsResponse, financialResponse] = await Promise.all([
        axios.get('/dashboard/analytics', { headers }),
        axios.get('/dashboard/event-financials', { headers })
      ]);
      
      console.log('🔍 DASHBOARD DEBUG: Got responses!');
      setAnalyticsData(analyticsResponse.data);
      
      // Handle different response formats for financial data
      const financialResponseData = financialResponse.data;
      console.log('🔍 DASHBOARD DEBUG - Financial Response Data:', financialResponseData); // Debug log
      
      if (Array.isArray(financialResponseData)) {
        // If response is direct array, wrap it in events property
        console.log('Financial data is array, sample event:', financialResponseData[0]); // Debug log
        setFinancialData({ events: financialResponseData });
      } else if (financialResponseData && financialResponseData.events) {
        // If response has events property
        console.log('Financial data has events property, sample event:', financialResponseData.events[0]); // Debug log
        setFinancialData(financialResponseData);
      } else {
        // Fallback: empty events array
        console.warn('Unexpected financial data format:', financialResponseData);
        setFinancialData({ events: [] });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
      // Set default empty data to prevent map errors
      setAnalyticsData({
        user_stats: {},
        collection_stats: {},
        booking_stats: {},
        recent_activity: []
      });
      setFinancialData({ events: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async (eventId) => {
    try {
      const expenseData = {
        ...expenseForm,
        event_id: eventId,
        amount: parseFloat(expenseForm.amount)
      };

      const headers = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};

      if (editingExpense) {
        await axios.put(`/events/${eventId}/expenses/${editingExpense.id}`, expenseData, { headers });
        toast.success('Expense updated successfully');
      } else {
        await axios.post(`/events/${eventId}/expenses`, expenseData, { headers });
        toast.success('Expense added successfully');
      }

      setExpenseForm({
        description: '',
        amount: '',
        category: 'other',
        date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      setEditingExpense(null);
      setIsExpenseDialogOpen(false);
      fetchDashboardData();
    } catch (error) {
      console.error('Error managing expense:', error);
      toast.error('Failed to save expense');
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      description: expense.description,
      amount: expense.amount.toString(),
      category: expense.category,
      date: expense.date,
      notes: expense.notes || ''
    });
    setIsExpenseDialogOpen(true);
  };

  const handleDeleteExpense = async (eventId, expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      const headers = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
      await axios.delete(`/events/${eventId}/expenses/${expenseId}`, { headers });
      toast.success('Expense deleted successfully');
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  const handleExportEventBookings = async (eventId, eventTitle) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please sign in again to export bookings');
        return;
      }

      toast.info('Preparing Excel export...');
      
      // Create a direct link to the export endpoint
      const exportUrl = `${axios.defaults.baseURL}/events/${eventId}/bookings/export?auth_token=${encodeURIComponent(token)}`;
      
      // Open in new window to trigger download
      window.open(exportUrl, '_blank');
      
      toast.success('Excel export started! Check your downloads.');
      
    } catch (error) {
      console.error('Error exporting event bookings:', error);
      toast.error('Failed to export event bookings');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const getCategoryIcon = (category) => {
    const categoryData = expenseCategories.find(cat => cat.value === category);
    return categoryData ? categoryData.label.split(' ')[0] : '📋';
  };

  if (!currentUser || currentUser.role !== 'Administrator') {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8">
          <CardContent className="text-center">
            <h2 className="text-xl font-semibold text-gray-600 mb-2">Access Denied</h2>
            <p className="text-gray-500">Administrator privileges required to view dashboard</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600">ACE Library, Mediateca and Cultural Events Manager</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => handleExportLoanHistory()}
            variant="outline"
            className="flex items-center gap-2 text-green-700 border-green-300 hover:bg-green-50"
          >
            <Download className="h-4 w-4" />
            Export Loan History
          </Button>
          <Button onClick={fetchDashboardData} variant="outline">
            <Activity className="h-4 w-4 mr-2" />
            Refresh Data
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {analyticsData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* User Statistics */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">User Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold">{analyticsData.user_stats.total_users}</div>
                  <div className="text-sm text-gray-600">Total Users</div>
                </div>
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">Internal (JRC Staff)</span>
                  <div className="text-right">
                    <span className="font-semibold">{analyticsData.user_stats.internal_users} users</span>
                    <div className="text-xs text-gray-500">({analyticsData.user_stats.internal_percentage}%)</div>
                  </div>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-orange-600">External Users</span>
                  <div className="text-right">
                    <span className="font-semibold">{analyticsData.user_stats.external_users} users</span>
                    <div className="text-xs text-gray-500">({analyticsData.user_stats.external_percentage}%)</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Media Collection */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Media Collection</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold">{analyticsData.collection_stats.total_media}</div>
                  <div className="text-sm text-gray-600">Total Items</div>
                </div>
                <BookOpen className="h-8 w-8 text-purple-600" />
              </div>
              <div className="mt-4 space-y-1">
                <div className="flex justify-between text-xs">
                  <span>📚 Books</span>
                  <span>{analyticsData.collection_stats.books}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span>💿 CDs</span>
                  <span>{analyticsData.collection_stats.cds}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span>📀 DVDs</span>
                  <span>{analyticsData.collection_stats.dvds}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Events & Bookings */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Cultural Events</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold">{analyticsData.collection_stats.events}</div>
                  <div className="text-sm text-gray-600">Total Events</div>
                </div>
                <Calendar className="h-8 w-8 text-indigo-600" />
              </div>
              <div className="mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Upcoming Events</span>
                  <span className="font-semibold">{analyticsData.booking_stats.upcoming_events}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Total Bookings</span>
                  <span className="font-semibold">{analyticsData.booking_stats.total_bookings}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity Summary */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold">{analyticsData.recent_activity.length}</div>
                  <div className="text-sm text-gray-600">Recent Bookings</div>
                </div>
                <Activity className="h-8 w-8 text-green-600" />
              </div>
              <div className="mt-4">
                <div className="text-sm text-gray-600">
                  Latest booking revenue: {analyticsData.recent_activity[0] ? formatCurrency(analyticsData.recent_activity[0].amount) : 'No recent activity'}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Activity Feed */}
      {analyticsData && analyticsData.recent_activity.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Recent Activity Feed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analyticsData?.recent_activity?.map((activity, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{activity.description}</p>
                    <p className="text-sm text-gray-600">{activity.date}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-green-600">{formatCurrency(activity.amount)}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      activity.status === 'paid' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {activity.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Event Financial Management */}
      {financialData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Event Financial Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {financialData?.events?.map((event) => {
                const isEventNew = isNewEvent(event);
                return (
                  <div key={event.event_id} className={`border rounded-lg p-4 transition-all ${
                    isEventNew ? 'border-green-300 bg-green-50 shadow-lg' : 'border-gray-200'
                  }`}>
                    {/* New Event Badge */}
                    {isEventNew && (
                      <div className="mb-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 animate-pulse">
                          ✨ Recently Added
                        </span>
                      </div>
                    )}
                    
                    {/* Event Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold">{event.event_title}</h3>
                        <p className="text-sm text-gray-600">{event.event_date}</p>
                        <p className="text-xs text-blue-600 mt-1">{event.bookings_count} bookings</p>
                      </div>
                    <div className="flex items-center gap-3">
                      {/* Export Bookings Button */}
                      {event.bookings_count > 0 && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleExportEventBookings(event.event_id, event.event_title)}
                          className="flex items-center gap-1"
                        >
                          <Download className="h-4 w-4" />
                          Export Bookings
                        </Button>
                      )}
                      <div className="text-right">
                        <div className={`text-lg font-bold ${
                          event.profit_loss >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {event.profit_loss >= 0 ? <TrendingUp className="inline h-4 w-4 mr-1" /> : <TrendingDown className="inline h-4 w-4 mr-1" />}
                          {formatCurrency(event.profit_loss)}
                        </div>
                        <div className="text-sm text-gray-600">
                          {event.profit_loss >= 0 ? 'Profit' : 'Loss'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Financial Summary */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-green-50 p-3 rounded">
                      <div className="text-sm text-green-600 font-medium">Income</div>
                      <div className="text-lg font-bold text-green-700">{formatCurrency(event.paid_income)}</div>
                      <div className="text-xs text-green-600">
                        {event.bookings_count} bookings • {formatCurrency(event.pending_income)} pending
                      </div>
                    </div>
                    <div className="bg-red-50 p-3 rounded">
                      <div className="text-sm text-red-600 font-medium">Expenses</div>
                      <div className="text-lg font-bold text-red-700">{formatCurrency(event.total_expenses)}</div>
                      <div className="text-xs text-red-600">{event.expenses.length} expense items</div>
                    </div>
                    <div className={`p-3 rounded ${
                      event.profit_loss >= 0 ? 'bg-blue-50' : 'bg-orange-50'
                    }`}>
                      <div className={`text-sm font-medium ${
                        event.profit_loss >= 0 ? 'text-blue-600' : 'text-orange-600'
                      }`}>
                        Net Result
                      </div>
                      <div className={`text-lg font-bold ${
                        event.profit_loss >= 0 ? 'text-blue-700' : 'text-orange-700'
                      }`}>
                        {formatCurrency(event.profit_loss)}
                      </div>
                    </div>
                  </div>

                  {/* Expenses Table */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium">Event Expenses</h4>
                      <Dialog open={isExpenseDialogOpen && selectedEventId === event.event_id} onOpenChange={setIsExpenseDialogOpen}>
                        <DialogTrigger asChild>
                          <Button 
                            size="sm" 
                            onClick={() => {
                              setSelectedEventId(event.event_id);
                              setEditingExpense(null);
                              setExpenseForm({
                                description: '',
                                amount: '',
                                category: 'other',
                                date: new Date().toISOString().split('T')[0],
                                notes: ''
                              });
                            }}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Expense
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>
                              {editingExpense ? 'Edit Expense' : 'Add New Expense'}
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <Input
                              placeholder="Expense description"
                              value={expenseForm.description}
                              onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                            />
                            <Input
                              type="number"
                              placeholder="Amount (€)"
                              value={expenseForm.amount}
                              onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                            />
                            <Select 
                              value={expenseForm.category} 
                              onValueChange={(value) => setExpenseForm({...expenseForm, category: value})}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent>
                                {expenseCategories.map(category => (
                                  <SelectItem key={category.value} value={category.value}>
                                    {category.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Input
                              type="date"
                              value={expenseForm.date}
                              onChange={(e) => setExpenseForm({...expenseForm, date: e.target.value})}
                            />
                            <Input
                              placeholder="Notes (optional)"
                              value={expenseForm.notes}
                              onChange={(e) => setExpenseForm({...expenseForm, notes: e.target.value})}
                            />
                            <div className="flex gap-2">
                              <Button 
                                onClick={() => handleAddExpense(event.event_id)}
                                className="flex-1"
                              >
                                {editingExpense ? 'Update Expense' : 'Add Expense'}
                              </Button>
                              <Button 
                                variant="outline" 
                                onClick={() => {
                                  setIsExpenseDialogOpen(false);
                                  setEditingExpense(null);
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {event.expenses.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Description</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {event.expenses?.map((expense) => (
                            <TableRow key={expense.id}>
                              <TableCell className="font-medium">
                                {expense.description}
                                {expense.notes && (
                                  <div className="text-sm text-gray-500">{expense.notes}</div>
                                )}
                              </TableCell>
                              <TableCell>
                                <span className="flex items-center gap-1">
                                  {getCategoryIcon(expense.category)}
                                  {expense.category}
                                </span>
                              </TableCell>
                              <TableCell>{expense.date}</TableCell>
                              <TableCell className="font-semibold text-red-600">
                                -{formatCurrency(expense.amount)}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedEventId(event.event_id);
                                      handleEditExpense(expense);
                                    }}
                                  >
                                    <Edit className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteExpense(event.event_id, expense.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        No expenses recorded for this event
                      </div>
                    )}
                  </div>
                </div>
              );
              })}

              {financialData?.events?.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No events found. Create some events to see financial data.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;